/**
 * Protocols permitted in user-supplied URLs (e.g. dashboard panel context links,
 * any href bound from configurable input). Excludes `javascript:`, `data:`,
 * `vbscript:`, `file:`, `blob:`, etc. that can lead to XSS or local-file disclosure.
 */
export const ALLOWED_URL_PROTOCOLS: readonly string[] = ['http:', 'https:', 'mailto:'];

/**
 * Validates that `url`'s protocol is in {@link ALLOWED_URL_PROTOCOLS}. Returns
 * the original string verbatim when the URL parses and its protocol is allowed,
 * otherwise returns `null`. The URL is **not** modified.
 *
 * Relative URLs (e.g. `/foo/bar`) are resolved against `window.location.origin`
 * for protocol determination, so they are accepted in the browser. In non-browser
 * environments a sentinel base origin is used; `null` is still returned for
 * disallowed schemes.
 *
 * Use this at every render site that binds an unknown URL to an `href`. The
 * caller is responsible for choosing fallback markup when this returns `null`
 * (typically: render the label without an anchor, or omit the link entirely).
 *
 * @example
 * validateUrlProtocol('https://example.com')           // 'https://example.com'
 * validateUrlProtocol('mailto:ops@example.com')        // 'mailto:ops@example.com'
 * validateUrlProtocol('/internal/path')                // '/internal/path'
 * validateUrlProtocol('javascript:alert(1)')           // null
 * validateUrlProtocol('  JaVaScRiPt:alert(1)  ')       // null
 * validateUrlProtocol('data:text/html,<script>...')    // null
 * validateUrlProtocol('not a url at all')              // null
 */
export function validateUrlProtocol(url: string): string | null {
  if (typeof url !== 'string') {
    return null;
  }

  const base = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';

  try {
    const parsed = new URL(url, base);
    if (ALLOWED_URL_PROTOCOLS.includes(parsed.protocol)) {
      return url;
    }
  } catch {
    return null;
  }

  return null;
}
